import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="crop-picker"
//
// Shows a 3:1 crop-box overlay on an image. Click or drag to reposition the
// crop center. Writes focal-point values (0.0–1.0) into two hidden inputs so
// they are submitted with the form.
export default class extends Controller {
  static targets = ["image", "cropBox", "focalX", "focalY"]
  static values = { ratio: { type: Number, default: 3 } }

  connect() {
    this.dragging = false
    if (this.imageTarget.complete) {
      this.updateCropBox()
    } else {
      this.imageTarget.addEventListener("load", () => this.updateCropBox(), { once: true })
    }
  }

  // ── pointer events ───────────────────────────────────────────────

  pointerdown(event) {
    event.preventDefault()
    this.element.setPointerCapture(event.pointerId)
    this.dragging = true
    this.setFocalFromEvent(event)
  }

  pointermove(event) {
    if (!this.dragging) return
    this.setFocalFromEvent(event)
  }

  pointerup() {
    this.dragging = false
  }

  // ── internals ────────────────────────────────────────────────────

  setFocalFromEvent(event) {
    const rect = this.imageTarget.getBoundingClientRect()
    const x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    const y = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))
    this.focalXTarget.value = x.toFixed(4)
    this.focalYTarget.value = y.toFixed(4)
    this.updateCropBox()
  }

  updateCropBox() {
    const img = this.imageTarget
    const focalX = parseFloat(this.focalXTarget.value) || 0.5
    const focalY = parseFloat(this.focalYTarget.value) || 0.5
    const ratio = this.ratioValue   // 3 (width : height)
    const imgW = img.offsetWidth
    const imgH = img.offsetHeight

    // Crop box that fills one axis of the displayed image at the target ratio.
    let cropW, cropH
    if (imgW / imgH > ratio) {
      cropH = imgH
      cropW = imgH * ratio
    } else {
      cropW = imgW
      cropH = imgW / ratio
    }

    // Center on focal point, clamped so the box stays inside the image.
    let left = focalX * imgW - cropW / 2
    let top = focalY * imgH - cropH / 2
    left = Math.max(0, Math.min(left, imgW - cropW))
    top = Math.max(0, Math.min(top, imgH - cropH))

    const box = this.cropBoxTarget
    box.style.left = `${left}px`
    box.style.top = `${top}px`
    box.style.width = `${cropW}px`
    box.style.height = `${cropH}px`
  }
}
